<template>
  <div class="logs-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-history"></i>
        Logs do Sistema
      </h1>
      <p class="page-subtitle">Monitore todas as ações realizadas no sistema</p>
    </div>

    <!-- Estatísticas -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-database"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.total_logs || 0 }}</h3>
          <p>Total de Logs</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon today">
          <i class="fas fa-calendar-day"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.logs_hoje || 0 }}</h3>
          <p>Hoje</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon week">
          <i class="fas fa-calendar-week"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.logs_semana || 0 }}</h3>
          <p>Esta Semana</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon month">
          <i class="fas fa-calendar-alt"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.logs_mes || 0 }}</h3>
          <p>Este Mês</p>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Tipo de Usuário</label>
          <select v-model="filters.isadmin" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="1">Administradores</option>
            <option value="0">Candidatos</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Tipo de Ação</label>
          <input 
            v-model="filters.tipo" 
            type="text" 
            placeholder="Ex: login, criar_concurso"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-group">
          <label>Usuário (Nome)</label>
          <input 
            v-model="filters.usuario_nome" 
            type="text" 
            placeholder="Buscar pelo nome do cadastro"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-group">
          <label>Descrição</label>
          <input 
            v-model="filters.descricao" 
            type="text" 
            placeholder="Buscar na descrição"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-group">
          <label>Data Início</label>
          <input 
            v-model="filters.data_inicio" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-group">
          <label>Data Fim</label>
          <input 
            v-model="filters.data_fim" 
            type="date" 
            class="form-control"
            @change="applyFilters"
          />
        </div>

        <div class="filter-group">
          <label>IP</label>
          <input 
            v-model="filters.ip" 
            type="text" 
            placeholder="Ex: 192.168.1.1"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportarLogs" class="btn btn-success" :disabled="exporting">
            <i :class="exporting ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
            Exportar CSV
          </button>
          <button @click="mostrarModalLimpar" class="btn btn-danger">
            <i class="fas fa-trash"></i>
            Limpar Antigos
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela de Logs -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando logs...</p>
      </div>

      <div v-else-if="logs.length === 0" class="empty-state">
        <i class="fas fa-history"></i>
        <h3>Nenhum log encontrado</h3>
        <p>Não há logs que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 80px">ID</th>
                <th style="width: 160px">Data/Hora</th>
                <th style="width: 120px">Tipo Usuário</th>
                <th style="width: 200px">Usuário</th>
                <th style="width: 180px">Tipo de Ação</th>
                <th>Descrição</th>
                <th style="width: 140px">IP</th>
                <th style="width: 100px" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td><strong>#{{ log.id }}</strong></td>
                <td>
                  <small>{{ formatarDataHora(log.created_at) }}</small>
                </td>
                <td>
                  <span :class="['badge', log.isadmin ? 'badge-admin' : 'badge-candidato']">
                    <i :class="log.isadmin ? 'fas fa-user-shield' : 'fas fa-user'"></i>
                    {{ log.isadmin ? 'Admin' : 'Candidato' }}
                  </span>
                </td>
                <td>
                  <div v-if="log.usuario_nome" class="user-info">
                    <strong>{{ log.usuario_nome }}</strong>
                    <small>{{ log.usuario_email }}</small>
                  </div>
                  <span v-else class="text-muted">N/A</span>
                </td>
                <td>
                  <span class="badge badge-tipo">{{ log.tipo }}</span>
                </td>
                <td>
                  <div class="descricao-truncate" :title="log.descricao">
                    {{ log.descricao }}
                  </div>
                </td>
                <td>
                  <code class="ip-badge">{{ log.ip }}</code>
                </td>
                <td class="text-center">
                  <button 
                    @click="visualizarDetalhes(log)" 
                    class="btn btn-sm btn-info"
                    title="Ver Detalhes"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-wrapper">
          <div class="pagination-info">
            Mostrando {{ logs.length }} de {{ pagination.total }} logs
          </div>
          <div class="pagination-controls">
            <button 
              @click="changePage(pagination.current_page - 1)" 
              :disabled="pagination.current_page === 1"
              class="btn btn-pagination"
            >
              <i class="fas fa-chevron-left"></i>
              Anterior
            </button>
            <span class="page-indicator">
              Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>
            <button 
              @click="changePage(pagination.current_page + 1)" 
              :disabled="pagination.current_page === pagination.last_page"
              class="btn btn-pagination"
            >
              Próxima
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          <div class="per-page-selector">
            <label>Logs por página:</label>
            <select v-model="perPage" @change="changePerPage" class="form-control form-control-sm">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Detalhes -->
    <div v-if="showModalDetalhes" class="modal-overlay" @click.self="fecharModalDetalhes">
      <div class="modal-content modal-detalhes">
        <div class="modal-header">
          <h2>
            <i class="fas fa-info-circle"></i>
            Detalhes do Log #{{ logSelecionado.id }}
          </h2>
          <button @click="fecharModalDetalhes" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="detalhes-grid">
            <div class="detalhe-item">
              <label>Data/Hora:</label>
              <span>{{ formatarDataHora(logSelecionado.created_at) }}</span>
            </div>

            <div class="detalhe-item">
              <label>Tipo de Usuário:</label>
              <span :class="['badge', logSelecionado.isadmin ? 'badge-admin' : 'badge-candidato']">
                <i :class="logSelecionado.isadmin ? 'fas fa-user-shield' : 'fas fa-user'"></i>
                {{ logSelecionado.isadmin ? 'Administrador' : 'Candidato' }}
              </span>
            </div>

            <div class="detalhe-item">
              <label>Usuário:</label>
              <div v-if="logSelecionado.usuario_nome">
                <strong>{{ logSelecionado.usuario_nome }}</strong>
                <br>
                <small>{{ logSelecionado.usuario_email }}</small>
              </div>
              <span v-else class="text-muted">N/A</span>
            </div>

            <div class="detalhe-item">
              <label>IP:</label>
              <code class="ip-badge">{{ logSelecionado.ip }}</code>
            </div>

            <div class="detalhe-item full-width">
              <label>Tipo de Ação:</label>
              <span class="badge badge-tipo">{{ logSelecionado.tipo }}</span>
            </div>

            <div class="detalhe-item full-width">
              <label>Descrição:</label>
              <p class="descricao-completa">{{ logSelecionado.descricao }}</p>
            </div>

            <div v-if="logSelecionado.dados_anteriores" class="detalhe-item full-width">
              <label>Dados Anteriores:</label>
              <pre class="json-display">{{ JSON.stringify(logSelecionado.dados_anteriores, null, 2) }}</pre>
            </div>

            <div v-if="logSelecionado.dados_novos" class="detalhe-item full-width">
              <label>Dados Novos:</label>
              <pre class="json-display">{{ JSON.stringify(logSelecionado.dados_novos, null, 2) }}</pre>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="fecharModalDetalhes" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Limpar Logs Antigos -->
    <div v-if="showModalLimpar" class="modal-overlay" @click.self="fecharModalLimpar">
      <div class="modal-content modal-limpar">
        <div class="modal-header">
          <h2>
            <i class="fas fa-exclamation-triangle"></i>
            Limpar Logs Antigos
          </h2>
          <button @click="fecharModalLimpar" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-circle"></i>
            Esta ação removerá permanentemente logs mais antigos que o período selecionado.
          </div>

          <div class="form-group">
            <label>Remover logs mais antigos que:</label>
            <select v-model="diasRetencao" class="form-control">
              <option :value="30">30 dias</option>
              <option :value="60">60 dias</option>
              <option :value="90">90 dias (recomendado)</option>
              <option :value="180">180 dias</option>
              <option :value="365">1 ano</option>
            </select>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="fecharModalLimpar" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Cancelar
          </button>
          <button @click="confirmarLimpar" class="btn btn-danger" :disabled="cleaning">
            <i :class="cleaning ? 'fas fa-spinner fa-spin' : 'fas fa-trash'"></i>
            Confirmar Limpeza
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LogsLista',
  
  data() {
    return {
      logs: [],
      estatisticas: {},
      loading: false,
      exporting: false,
      cleaning: false,
      filters: {
        isadmin: '',
        usuario_nome: '',
        tipo: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        ip: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0
      },
      perPage: 50,
      searchTimeout: null,
      showModalDetalhes: false,
      showModalLimpar: false,
      logSelecionado: {},
      diasRetencao: 90
    };
  },

  mounted() {
    this.carregarEstatisticas();
    this.carregarLogs();
  },

  methods: {
    async carregarLogs(page = 1) {
      this.loading = true;
      try {
        const params = {
          ...this.filters,
          page,
          per_page: this.perPage
        };

        // Remover parâmetros vazios
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await axios.get('/ps/logs', {
          params
        });

        this.logs = response.data.logs;
        this.pagination = response.data.pagination;

      } catch (error) {
        console.error('Erro ao carregar logs:', error);
        this.$sweet.showError('Erro ao carregar logs. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    async carregarEstatisticas() {
      try {
        const params = {};
        if (this.filters.data_inicio) params.data_inicio = this.filters.data_inicio;
        if (this.filters.data_fim) params.data_fim = this.filters.data_fim;

        const response = await axios.get('/ps/logs/estatisticas', {
          params
        });

        this.estatisticas = response.data;

      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
      }
    },

    applyFilters() {
      this.pagination.current_page = 1;
      this.carregarLogs(1);
      this.carregarEstatisticas();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },

    clearFilters() {
      this.filters = {
        isadmin: '',
        usuario_nome: '',
        tipo: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        ip: ''
      };
      this.applyFilters();
    },

    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.carregarLogs(page);
      }
    },

    changePerPage() {
      this.pagination.current_page = 1;
      this.carregarLogs(1);
    },

    async exportarLogs() {
      this.exporting = true;
      try {
        const params = { ...this.filters };
        
        // Remover parâmetros vazios
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await axios.get('/ps/logs/exportar', {
          params,
          responseType: 'blob'
        });

        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `logs_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$sweet.showSuccess('Logs exportados com sucesso!');

      } catch (error) {
        console.error('Erro ao exportar logs:', error);
        this.$sweet.showError('Erro ao exportar logs. Tente novamente.');
      } finally {
        this.exporting = false;
      }
    },

    visualizarDetalhes(log) {
      this.logSelecionado = { ...log };
      this.showModalDetalhes = true;
    },

    fecharModalDetalhes() {
      this.showModalDetalhes = false;
      this.logSelecionado = {};
    },

    mostrarModalLimpar() {
      this.showModalLimpar = true;
    },

    fecharModalLimpar() {
      this.showModalLimpar = false;
      this.diasRetencao = 90;
    },

    async confirmarLimpar() {
      const result = await this.$sweet.showConfirm(
        'Confirmar Limpeza',
        `Deseja realmente remover logs com mais de ${this.diasRetencao} dias?<br><small>Esta ação não pode ser desfeita.</small>`
      );

      if (!result.isConfirmed) return;

      this.cleaning = true;
      try {
        const response = await axios.delete('/ps/logs/limpar', {
          params: { dias: this.diasRetencao }
        });

        this.$sweet.showSuccess(`${response.data.logs_removidos} logs foram removidos com sucesso!`);
        this.fecharModalLimpar();
        this.carregarLogs();
        this.carregarEstatisticas();

      } catch (error) {
        console.error('Erro ao limpar logs:', error);
        this.$sweet.showError('Erro ao limpar logs. Tente novamente.');
      } finally {
        this.cleaning = false;
      }
    },

    formatarDataHora(dataHora) {
      if (!dataHora) return 'N/A';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof dataHora === 'string' && dataHora.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dataHora.split('-');
          return `${day}/${month}/${year} 00:00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof dataHora === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = dataHora.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds || '00'}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds || '00'}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(dataHora);
        if (isNaN(dateObj.getTime())) return 'N/A';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        const segundo = String(dateObj.getSeconds()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}:${segundo}`;
      } catch (error) {
        return 'N/A';
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.logs-lista
  padding: 2rem

.page-header
  margin-bottom: 2rem

  .page-title
    display: flex
    align-items: center
    gap: 1rem
    font-size: 2rem
    font-weight: 700
    color: #2c3e50
    margin-bottom: 0.5rem

    i
      color: #667eea

  .page-subtitle
    color: #6c757d
    font-size: 1rem

.stats-grid
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr))
  gap: 1.5rem
  margin-bottom: 2rem

.stat-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  display: flex
  align-items: center
  gap: 1rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)
  transition: transform 0.3s ease, box-shadow 0.3s ease

  &:hover
    transform: translateY(-4px)
    box-shadow: 0 4px 16px rgba(0,0,0,0.12)

  .stat-icon
    width: 60px
    height: 60px
    border-radius: 12px
    display: flex
    align-items: center
    justify-content: center
    font-size: 1.5rem
    color: white

    &.total
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)

    &.today
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%)

    &.week
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)

    &.month
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)

  .stat-content
    h3
      font-size: 1.8rem
      font-weight: 700
      margin: 0
      color: #2c3e50

    p
      margin: 0
      color: #6c757d
      font-size: 0.9rem

.filters-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  margin-bottom: 2rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)

.filters-row
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))
  gap: 1rem
  align-items: end

.filter-group
  label
    display: block
    font-weight: 600
    margin-bottom: 0.5rem
    color: #495057
    font-size: 0.9rem

  .form-control
    width: 100%
    padding: 0.6rem
    border: 1px solid #ced4da
    border-radius: 8px
    font-size: 0.9rem
    transition: border-color 0.3s ease, box-shadow 0.3s ease

    &:focus
      outline: none
      border-color: #667eea
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1)

.filter-actions
  display: flex
  gap: 0.5rem
  flex-wrap: wrap

  .btn
    padding: 0.6rem 1.2rem
    border: none
    border-radius: 8px
    font-weight: 600
    cursor: pointer
    transition: all 0.3s ease
    display: inline-flex
    align-items: center
    gap: 0.5rem

    &:disabled
      opacity: 0.6
      cursor: not-allowed

    &.btn-secondary
      background: #6c757d
      color: white

      &:hover:not(:disabled)
        background: #5a6268

    &.btn-success
      background: #28a745
      color: white

      &:hover:not(:disabled)
        background: #218838

    &.btn-danger
      background: #dc3545
      color: white

      &:hover:not(:disabled)
        background: #c82333

.table-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)

.loading-state, .empty-state
  text-align: center
  padding: 4rem 2rem
  color: #6c757d

  i
    font-size: 3rem
    margin-bottom: 1rem
    opacity: 0.5

  h3
    font-size: 1.5rem
    margin-bottom: 0.5rem

  p
    font-size: 1rem

.table-responsive
  overflow-x: auto

.table
  width: 100%
  border-collapse: collapse
  font-size: 0.9rem

  thead
    background: #f8f9fa
    border-bottom: 2px solid #dee2e6

    th
      padding: 1rem 0.75rem
      text-align: left
      font-weight: 600
      color: #495057
      white-space: nowrap

  tbody
    tr
      border-bottom: 1px solid #dee2e6
      transition: background-color 0.2s ease

      &:hover
        background-color: #f8f9fa

    td
      padding: 1rem 0.75rem
      vertical-align: middle

.badge
  display: inline-flex
  align-items: center
  gap: 0.4rem
  padding: 0.4rem 0.8rem
  border-radius: 6px
  font-size: 0.8rem
  font-weight: 600
  white-space: nowrap

  &.badge-admin
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    color: white

  &.badge-candidato
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)
    color: white

  &.badge-tipo
    background: #e9ecef
    color: #495057

.user-info
  display: flex
  flex-direction: column
  gap: 0.2rem

  strong
    font-weight: 600
    color: #2c3e50

  small
    color: #6c757d
    font-size: 0.85rem

.descricao-truncate
  max-width: 300px
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.ip-badge
  background: #f8f9fa
  padding: 0.3rem 0.6rem
  border-radius: 4px
  font-size: 0.85rem
  color: #495057
  border: 1px solid #dee2e6

.btn-sm
  padding: 0.4rem 0.8rem
  font-size: 0.85rem
  border: none
  border-radius: 6px
  cursor: pointer
  transition: all 0.3s ease

  &.btn-info
    background: #17a2b8
    color: white

    &:hover
      background: #138496

.pagination-wrapper
  display: flex
  justify-content: space-between
  align-items: center
  margin-top: 1.5rem
  padding-top: 1.5rem
  border-top: 1px solid #dee2e6
  flex-wrap: wrap
  gap: 1rem

.pagination-info
  color: #6c757d
  font-size: 0.9rem

.pagination-controls
  display: flex
  align-items: center
  gap: 1rem

  .btn-pagination
    padding: 0.5rem 1rem
    border: 1px solid #dee2e6
    background: white
    border-radius: 6px
    cursor: pointer
    transition: all 0.3s ease

    &:hover:not(:disabled)
      background: #f8f9fa
      border-color: #667eea

    &:disabled
      opacity: 0.5
      cursor: not-allowed

  .page-indicator
    font-weight: 600
    color: #495057

.per-page-selector
  display: flex
  align-items: center
  gap: 0.5rem

  label
    font-size: 0.9rem
    color: #6c757d
    white-space: nowrap

  .form-control-sm
    width: 80px
    padding: 0.4rem
    border: 1px solid #ced4da
    border-radius: 6px

.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  display: flex
  align-items: center
  justify-content: center
  z-index: 9999
  padding: 2rem

.modal-content
  background: white
  border-radius: 12px
  width: 100%
  max-width: 800px
  max-height: 90vh
  overflow-y: auto
  box-shadow: 0 10px 40px rgba(0,0,0,0.2)
  animation: slideInUp 0.3s ease

  &.modal-limpar
    max-width: 500px

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(-30px)
  to
    opacity: 1
    transform: translateY(0)

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 1.5rem
  border-bottom: 1px solid #dee2e6

  h2
    display: flex
    align-items: center
    gap: 0.8rem
    margin: 0
    font-size: 1.5rem
    color: #2c3e50

    i
      color: #667eea

  .btn-close
    background: none
    border: none
    font-size: 1.5rem
    color: #6c757d
    cursor: pointer
    transition: color 0.3s ease

    &:hover
      color: #dc3545

.modal-body
  padding: 1.5rem

.detalhes-grid
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 1.5rem

.detalhe-item
  &.full-width
    grid-column: 1 / -1

  label
    display: block
    font-weight: 600
    color: #495057
    margin-bottom: 0.5rem
    font-size: 0.9rem

  span, p
    color: #2c3e50

.descricao-completa
  background: #f8f9fa
  padding: 1rem
  border-radius: 8px
  margin: 0

.json-display
  background: #2d3436
  color: #00ff7f
  padding: 1rem
  border-radius: 8px
  overflow-x: auto
  font-size: 0.85rem
  margin: 0

.alert
  padding: 1rem
  border-radius: 8px
  margin-bottom: 1.5rem
  display: flex
  align-items: center
  gap: 0.8rem

  &.alert-warning
    background: #fff3cd
    border: 1px solid #ffc107
    color: #856404

.modal-footer
  padding: 1.5rem
  border-top: 1px solid #dee2e6
  display: flex
  justify-content: flex-end
  gap: 1rem

  .btn
    padding: 0.6rem 1.5rem
    border: none
    border-radius: 8px
    font-weight: 600
    cursor: pointer
    transition: all 0.3s ease
    display: inline-flex
    align-items: center
    gap: 0.5rem

    &:disabled
      opacity: 0.6
      cursor: not-allowed

@media (max-width: 768px)
  .logs-lista
    padding: 1rem

  .stats-grid
    grid-template-columns: 1fr

  .filters-row
    grid-template-columns: 1fr

  .detalhes-grid
    grid-template-columns: 1fr

  .pagination-wrapper
    flex-direction: column
    align-items: stretch

  .pagination-controls
    justify-content: center
</style>

